"use client";

import { useState, useEffect, useMemo, Suspense } from "react";
import { useAuth } from "@/lib/hooks/useAuth";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import {
  ArrowLeft,
  ChevronRight,
  Headphones,
  Loader2,
  Plus,
  Receipt,
} from "lucide-react";
import { useSession } from "next-auth/react";
import { getDb } from "@/lib/api/firebase-lazy";
import {
  collection,
  query,
  where,
  getDocs,
  limit,
  Timestamp,
} from "firebase/firestore";
import {
  FeedbackRequestDetailDrawer,
  type FeedbackRequestItem,
} from "@/components/expert-feedback/FeedbackRequestDetailDrawer";
import { getFeedbackOptionLabel } from "@/lib/feedback/feedbackOptionLabels";
import {
  getFeedbackStatusClass,
  getFeedbackStatusLabel,
  isFeedbackRequestInProgress,
} from "@/lib/feedbackStatus";

type StatusFilter = "all" | "active" | "completed" | "cancelled";

function normalizeDate(value: unknown): Date | undefined {
  if (!value) return undefined;
  if (typeof value === "object") {
    const maybeTimestamp = value as { toDate?: () => Date };
    if (typeof maybeTimestamp.toDate === "function") {
      return maybeTimestamp.toDate();
    }
    if (value instanceof Date) return value;
  }
  if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) return parsed;
  }
  return undefined;
}

function mapFeedbackDoc(
  docId: string,
  uid: string,
  data: Record<string, unknown>
): FeedbackRequestItem {
  return {
    id: docId,
    userId: uid,
    status: (data.status as string) ?? "pending",
    paymentStatus: data.paymentStatus as string | undefined,
    requestType: "feedback",
    category: (data.category as string) ?? undefined,
    productName: (data.productName as string) ?? undefined,
    optionLabel: (data.optionLabel as string) ?? undefined,
    feedbackOption: data.feedbackOption as number | string | undefined,
    songTitle: (data.songTitle as string) ?? undefined,
    fileName: (data.fileName as string) ?? undefined,
    messageToExpert: (data.messageToExpert as string) ?? undefined,
    paidAmount:
      typeof data.paidAmount === "number" ? data.paidAmount : undefined,
    originalPrice:
      typeof data.originalPrice === "number" ? data.originalPrice : undefined,
    paymentMerchantUid: (data.paymentMerchantUid as string) ?? undefined,
    paymentImpUid: (data.paymentImpUid as string) ?? undefined,
    paymentProvider: (data.paymentProvider as string) ?? undefined,
    paymentMethod: (data.paymentMethod as string) ?? undefined,
    paymentVerifiedAt: normalizeDate(data.paymentVerifiedAt),
    requestedAt:
      normalizeDate(data.requestedAt) ?? normalizeDate(data.createdAt),
    createdAt: normalizeDate(data.createdAt),
    completedAt: normalizeDate(data.completedAt),
    cancelledAt: normalizeDate(data.cancelledAt),
    audioUrl: (data.audioUrl as string) ?? undefined,
    videoUrl: (data.videoUrl as string) ?? undefined,
    fileUrl: (data.fileUrl as string) ?? undefined,
    audioFileName: (data.audioOriginalName as string) ?? undefined,
    videoFileName: (data.videoFileName as string) ?? undefined,
    attachmentFileName: (data.attachmentFileName as string) ?? undefined,
    assignedProId: (data.assignedProId as string) ?? undefined,
    assignedProName: (data.assignedProName as string) ?? undefined,
    expertId: (data.assignedProId as string) ?? (data.expertId as string),
    expertName:
      (data.assignedProName as string) ?? (data.expertName as string),
  };
}

function ExpertAnalysisContent() {
  const { user, loading: authLoading } = useAuth();
  const { data: session, status: sessionStatus } = useSession();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [requests, setRequests] = useState<FeedbackRequestItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] =
    useState<FeedbackRequestItem | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const effectiveUserId =
    user?.uid ||
    (session?.user as { uid?: string; id?: string; userId?: string })?.uid ||
    (session?.user as { uid?: string; id?: string; userId?: string })?.id ||
    (session?.user as { uid?: string; id?: string; userId?: string })?.userId ||
    null;

  const isAuthResolving = authLoading || sessionStatus === "loading";

  const loadRequests = async (uid: string) => {
    setLoading(true);
    try {
      const db = getDb();
      const snapshot = await getDocs(
        query(
          collection(db, "expert-feedback-requests"),
          where("userId", "==", uid),
          limit(100)
        )
      );

      const items = snapshot.docs
        .map(doc => mapFeedbackDoc(doc.id, uid, doc.data() as Record<string, unknown>))
        .sort((a, b) => {
          const at = (a.requestedAt || a.createdAt)?.getTime() || 0;
          const bt = (b.requestedAt || b.createdAt)?.getTime() || 0;
          return bt - at;
        });

      setRequests(items);
    } catch (error) {
      console.error("피드백 신청 내역 로드 실패:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthResolving) return;
    if (!effectiveUserId) {
      router.replace("/auth/login?callbackUrl=/profile/expert-analysis");
      return;
    }
    void loadRequests(effectiveUserId);
  }, [effectiveUserId, isAuthResolving, router]);

  useEffect(() => {
    const openId = searchParams.get("requestId");
    if (!openId || requests.length === 0) return;
    const found = requests.find(r => r.id === openId);
    if (found) setSelectedRequest(found);
  }, [searchParams, requests]);

  const filteredRequests = useMemo(() => {
    return requests.filter(request => {
      if (statusFilter === "all") return true;
      if (statusFilter === "active") {
        return isFeedbackRequestInProgress(request.status);
      }
      if (statusFilter === "completed") return request.status === "completed";
      if (statusFilter === "cancelled") return request.status === "cancelled";
      return true;
    });
  }, [requests, statusFilter]);

  const counts = useMemo(
    () => ({
      all: requests.length,
      active: requests.filter(r => isFeedbackRequestInProgress(r.status)).length,
      completed: requests.filter(r => r.status === "completed").length,
      cancelled: requests.filter(r => r.status === "cancelled").length,
    }),
    [requests]
  );

  const formatShortDate = (date?: Date) => {
    if (!date) return "-";
    return date.toLocaleDateString("ko-KR", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isAuthResolving || loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
      </div>
    );
  }

  return (
    <div className="pb-12 text-gray-900">
      <header className="rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="px-4 py-4 sm:px-6">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => router.back()}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4" />
              뒤로
            </button>
            <div className="text-center flex-1">
              <h1 className="text-base font-semibold">프로 피드백 신청내역</h1>
              <p className="mt-0.5 text-[11px] text-gray-500">
                결제 · 접수 · 피드백 진행 상태
              </p>
            </div>
            <Link
              href="/expert-feedback"
              className="flex items-center gap-1 text-xs font-medium text-violet-600 hover:text-violet-700"
            >
              <Plus className="w-3.5 h-3.5" />
              신청
            </Link>
          </div>

          <div className="mt-4 flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
            {(
              [
                ["all", "전체"],
                ["active", "진행중"],
                ["completed", "완료"],
                ["cancelled", "취소"],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setStatusFilter(key)}
                className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
                  statusFilter === key
                    ? "bg-violet-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {label} ({counts[key]})
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="space-y-3 py-5">
        <div className="flex items-start gap-3 rounded-xl border border-violet-100 bg-violet-50/60 px-4 py-3">
          <Receipt className="w-4 h-4 text-violet-600 mt-0.5 shrink-0" />
          <p className="text-xs leading-relaxed text-gray-600">
            <span className="font-medium text-gray-900">나의 리포트</span>는
            프로가 작성을 마친 피드백 결과만 모아둔 곳이에요. 신청·결제·진행
            상태는 이 페이지에서 확인하세요.
          </p>
          <Link
            href="/profile/reports?tab=reports"
            className="shrink-0 whitespace-nowrap text-xs text-violet-600 hover:text-violet-700"
          >
            리포트 보기
          </Link>
        </div>

        {filteredRequests.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-16 text-center">
            <Headphones className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="mb-1 font-medium text-gray-800">
              {statusFilter === "all"
                ? "아직 신청 내역이 없습니다"
                : "해당 상태의 신청이 없습니다"}
            </p>
            <p className="mb-6 text-sm text-gray-500">
              프로에게 직접 피드백을 받아보세요.
            </p>
            <Link
              href="/expert-feedback"
              className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-violet-500"
            >
              <Plus className="w-4 h-4" />
              피드백 신청하기
            </Link>
          </div>
        ) : (
          filteredRequests.map(request => {
            const title =
              request.songTitle ||
              request.fileName ||
              request.category ||
              "피드백 신청";
            const option =
              request.optionLabel ||
              getFeedbackOptionLabel(request.feedbackOption);
            return (
              <button
                key={request.id}
                type="button"
                onClick={() => setSelectedRequest(request)}
                className="w-full rounded-2xl border border-gray-200 bg-white p-4 text-left shadow-sm transition hover:border-violet-300 hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${getFeedbackStatusClass(request.status)}`}
                      >
                        {getFeedbackStatusLabel(request.status)}
                      </span>
                      {request.paymentStatus === "paid" && (
                        <span className="text-[10px] text-emerald-600 font-medium">
                          결제완료
                        </span>
                      )}
                    </div>
                    <h3 className="truncate font-semibold text-gray-900">{title}</h3>
                    <p className="text-xs text-gray-500 mt-1">
                      {request.category} · {option}
                    </p>
                    <p className="mt-2 text-[11px] text-gray-400">
                      {formatShortDate(request.requestedAt || request.createdAt)}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    {typeof request.paidAmount === "number" && (
                      <p className="text-sm font-bold text-gray-900">
                        {request.paidAmount.toLocaleString()}원
                      </p>
                    )}
                    <ChevronRight className="w-4 h-4 ml-auto mt-2 text-gray-300" />
                  </div>
                </div>
              </button>
            );
          })
        )}
      </main>

      {selectedRequest && (
        <FeedbackRequestDetailDrawer
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onCancelled={requestId => {
            setRequests(prev =>
              prev.map(item =>
                item.id === requestId
                  ? {
                      ...item,
                      status: "cancelled",
                      paymentStatus:
                        item.paymentStatus === "paid"
                          ? "refunded"
                          : item.paymentStatus,
                    }
                  : item
              )
            );
          }}
        />
      )}
    </div>
  );
}

export default function ExpertAnalysisPage() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-[40vh] items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
        </div>
      }
    >
      <ExpertAnalysisContent />
    </Suspense>
  );
}
